import { useEffect } from 'react'
import { atom, useRecoilState, useSetRecoilState, useResetRecoilState } from 'recoil'

import { SitesSelection, SitesUnscopeFilters, SitesUnscopeSort } from './types'

const EMPTY_SELECTION: SitesSelection = { mode: 'ids', ids: [] }

const filtersState = atom<SitesUnscopeFilters>({
  key: 'AdminSitesFilters',
  default: {
    verbatim: '',
    account: null,
    state: [],
    has_active_project: null,
    has_process: null,
    visibility_in_center: null
  }
})

const sortState = atom<SitesUnscopeSort>({
  key: 'AdminSitesSort',
  default: {
    by: 'name',
    direction: 'asc'
  }
})

const selectionState = atom<SitesSelection>({
  key: 'AdminSitesSelection',
  default: EMPTY_SELECTION
})

const lastCheckedIdState = atom<string | null>({
  key: 'AdminSitesLastCheckedId',
  default: null
})

const useFilters = () => useRecoilState(filtersState)

export const toSitesUnscopeVariables = (filters: SitesUnscopeFilters) => {
  const { account, ...otherFilters } = filters
  return { ...otherFilters, sf_parent_id: account?.sf_id ?? null }
}

export const useSort = () => {
  const [sort, setSort] = useRecoilState(sortState)

  const toggleSort = (by: string) => {
    if (sort.by === by) {
      setSort({ by, direction: sort.direction === 'asc' ? 'desc' : 'asc' })
      return
    }
    setSort({ by, direction: 'asc' })
  }

  return [sort, toggleSort] as const
}

export const useSelection = () => {
  const [selection, setSelection] = useRecoilState(selectionState)
  const [lastCheckedId, setLastCheckedId] = useRecoilState(lastCheckedIdState)

  const isAllSelected = selection.mode === 'all'
  const selectedIds = selection.mode === 'ids' ? selection.ids : []
  const hasSelection = isAllSelected || selectedIds.length > 0

  const isSelected = (id: string) => isAllSelected || selectedIds.includes(id)

  const areAllSelected = (ids: string[]) => ids.length > 0 && ids.every(isSelected)

  const selectIds = (ids: string[]) => setSelection({ mode: 'ids', ids })

  const selectAll = () => setSelection({ mode: 'all' })

  const clear = () => {
    setSelection(EMPTY_SELECTION)
    setLastCheckedId(null)
  }

  const selectRange = (id: string, fromId: string, loadedIds: string[]) => {
    const fromIndex = loadedIds.indexOf(fromId)
    const toIndex = loadedIds.indexOf(id)
    const rangeIds = loadedIds.slice(Math.min(fromIndex, toIndex), Math.max(fromIndex, toIndex) + 1)
    selectIds([...selectedIds, ...rangeIds.filter(rangeId => !selectedIds.includes(rangeId))])
    setLastCheckedId(id)
  }

  const toggle = (id: string, loadedIds: string[], isRangeSelection = false) => {
    if (isAllSelected) {
      selectIds(loadedIds.filter(loadedId => loadedId !== id))
      setLastCheckedId(null)
      return
    }
    if (isRangeSelection && lastCheckedId && loadedIds.includes(lastCheckedId)) {
      selectRange(id, lastCheckedId, loadedIds)
      return
    }
    if (selectedIds.includes(id)) {
      selectIds(selectedIds.filter(selectedId => selectedId !== id))
      return
    }
    selectIds([...selectedIds, id])
    setLastCheckedId(id)
  }

  return {
    isAllSelected,
    selectedIds,
    hasSelection,
    isSelected,
    areAllSelected,
    selectIds,
    selectAll,
    clear,
    toggle
  }
}

export const useResetSelectionOnFiltersChange = () => {
  const [filters] = useFilters()
  const setSelection = useSetRecoilState(selectionState)
  const setLastCheckedId = useSetRecoilState(lastCheckedIdState)

  useEffect(() => {
    setSelection(EMPTY_SELECTION)
    setLastCheckedId(null)
  }, [filters, setSelection, setLastCheckedId])
}

export const useResetFilters = () => {
  return useResetRecoilState(filtersState)
}

export default useFilters
